import React, { useState, useEffect, Suspense, lazy } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { useMunicipalities } from '../../hooks/useMunicipalities';
import { useReunionesForCalendar } from '../../hooks/useReuniones';
import { useIsMobile, useIsTablet, useIsDesktop } from '../../hooks/useMediaQuery';
import { useIdleTimeout } from '../../hooks/useIdleTimeout';
import { useModalManager } from '../../hooks/useModalManager';
import { useCalendarLogic } from '../../hooks/useCalendarLogic';
import { useClickOutside } from '../../hooks/useClickOutside';
import { initializeLookupMaps, reunionToCalendarEvent } from '../../lib/adapters';
import { TabId } from '../shared/BottomNav';
import { logger } from '../../lib/logger';
import { MUNICIPALITIES as STATIC_MUNICIPALITIES } from '../../constants';

// Layout components
import { Sidebar } from './Sidebar';
import { Header } from '../shared/Header';
import { BottomNav } from '../shared/BottomNav';
import { IdleTimeoutWarning } from '../shared/IdleTimeoutWarning';

// Modal components
import { ReunionModal } from '../modals/ReunionModal';
import { PropuestaModal } from '../modals/PropuestaModal';
import { GestionModal } from '../modals/GestionModal';
import { DayViewModal } from '../modals/DayViewModal';

// View components (lazy loaded for code splitting)
import { AgendaView } from './AgendaView';
const BitacoraView = lazy(() => import('../views/BitacoraView').then(m => ({ default: m.BitacoraView })));
const ReunionesView = lazy(() => import('../views/ReunionesView').then(m => ({ default: m.ReunionesView })));
const PropuestasView = lazy(() => import('../views/PropuestasView').then(m => ({ default: m.PropuestasView })));
const GestionesView = lazy(() => import('../views/GestionesView').then(m => ({ default: m.GestionesView })));
const ElectoralView = lazy(() => import('../views/ElectoralView').then(m => ({ default: m.ElectoralView })));
const SettingsView = lazy(() => import('../views/SettingsView').then(m => ({ default: m.SettingsView })));

// Logo URL constant
const LOGO_URL = `${import.meta.env.VITE_SUPABASE_URL}/storage/v1/object/public/Logos/Black%20and%20White%20Circular%20Art%20&%20Design%20Logo(1).svg`;

/**
 * ViewSuspenseFallback - Loading indicator shown while lazy-loading view components
 */
function ViewSuspenseFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="w-12 h-12 border-4 border-[#5D1828] border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

/**
 * AppLayout - Main application layout component
 *
 * Orchestrates:
 * - Sidebar navigation (desktop/tablet)
 * - Header with user profile
 * - Bottom navigation (mobile)
 * - Tab-based content views
 * - Modal management (reuniones, propuestas, gestiones, day view)
 * - Session timeout warnings
 * - Calendar integration
 */
export function AppLayout() {
  const { signOut } = useAuth();
  const { municipalities } = useMunicipalities();

  // Responsive detection
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isDesktop = useIsDesktop();

  // Session timeout: DISABLED per user request (was causing complaints)
  // Users can remain logged in indefinitely until manual logout
  const {
    showWarning: showIdleWarning,
    remainingTime: idleRemainingTime,
    dismissWarning: dismissIdleWarning,
  } = useIdleTimeout(signOut, {
    warningTimeMs: 25 * 60 * 1000,
    logoutTimeMs: 5 * 60 * 1000,
    enabled: false, // DISABLED - no auto-logout
  });

  // Active tab state (persisted to sessionStorage)
  const [activeTab, setActiveTab] = useState<TabId>(() => {
    const saved = sessionStorage.getItem('activeTab');
    return (saved as TabId) || 'agenda';
  });

  // Sidebar state (collapsed on tablet, expanded on desktop)
  const [isSidebarOpen, setIsSidebarOpen] = useState(!isTablet);

  // Profile menu state
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useClickOutside<HTMLDivElement>(() => setIsProfileMenuOpen(false));

  // Electoral view state (persisted to sessionStorage)
  const [electoralViewMode, setElectoralViewMode] = useState<'menu' | 'workspace'>(() => {
    const saved = sessionStorage.getItem('electoralViewMode');
    return (saved as 'menu' | 'workspace') || 'menu';
  });
  const [selectedElectoralType, setSelectedElectoralType] = useState<string | null>(() => {
    const saved = sessionStorage.getItem('selectedElectoralType');
    return saved || null;
  });

  // Calendar logic hook
  const calendar = useCalendarLogic();

  // Modal management hook
  const modals = useModalManager();

  // Load reuniones for calendar view
  const { reuniones: calendarReuniones, refresh: refreshCalendar } = useReunionesForCalendar(
    calendar.currentDate.getFullYear(),
    calendar.currentDate.getMonth()
  );

  // Municipalities array (from context or fallback to static)
  const MUNICIPALITIES = municipalities.length > 0
    ? municipalities.map(m => m.name)
    : STATIC_MUNICIPALITIES;

  // Save activeTab to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('activeTab', activeTab);
  }, [activeTab]);

  // Save electoral state to sessionStorage
  useEffect(() => {
    sessionStorage.setItem('electoralViewMode', electoralViewMode);
  }, [electoralViewMode]);

  useEffect(() => {
    if (selectedElectoralType) {
      sessionStorage.setItem('selectedElectoralType', selectedElectoralType);
    } else {
      sessionStorage.removeItem('selectedElectoralType');
    }
  }, [selectedElectoralType]);

  // Update sidebar state on viewport changes
  useEffect(() => {
    if (isTablet) {
      setIsSidebarOpen(false);
    } else if (isDesktop) {
      setIsSidebarOpen(true);
    }
  }, [isTablet, isDesktop]);

  // Auto-collapse sidebar on mobile
  useEffect(() => {
    setIsSidebarOpen(!isMobile);
  }, [isMobile]);

  // Update DayViewModal events when calendar data changes
  useEffect(() => {
    if (modals.isDayViewModalOpen && modals.selectedDayDate) {
      const calendarEvents = calendarReuniones.map(reunionToCalendarEvent);
      const dayEvents = calendarEvents.filter(e => e.date === modals.selectedDayDate);
      modals.setSelectedDayEvents(dayEvents);
    }
  }, [calendarReuniones, modals.isDayViewModalOpen, modals.selectedDayDate]);

  // Initialize lookup maps when data loads
  useEffect(() => {
    if (municipalities.length > 0) {
      initializeLookupMaps({
        municipalities,
        reuniones: calendarReuniones.map(r => ({ id: r.id, title: r.title })),
      });
    }
  }, [municipalities, calendarReuniones]);

  // Listen for custom events from ReunionModal to open linked items
  useEffect(() => {
    const handleOpenPropuestaDetails = (e: CustomEvent) => {
      const propuesta = e.detail;
      logger.log('[AppLayout] Opening propuesta details from reunion:', propuesta);
      modals.openPropuestaDetails(propuesta, modals.selectedReunionData || undefined);
    };

    const handleOpenGestionDetails = (e: CustomEvent) => {
      const gestion = e.detail;
      logger.log('[AppLayout] Opening gestion details from reunion:', gestion);
      modals.openGestionDetails(gestion, modals.selectedReunionData || undefined);
    };

    window.addEventListener('openPropuestaDetails', handleOpenPropuestaDetails as EventListener);
    window.addEventListener('openGestionDetails', handleOpenGestionDetails as EventListener);

    return () => {
      window.removeEventListener('openPropuestaDetails', handleOpenPropuestaDetails as EventListener);
      window.removeEventListener('openGestionDetails', handleOpenGestionDetails as EventListener);
    };
  }, [modals.selectedReunionData]);

  // Action handlers
  const handleActionClick = (actionId: string) => {
    if (actionId === 'reunion') modals.openReunionModal();
    else if (actionId === 'propuesta') modals.openPropuestaModal();
    else if (actionId === 'gestion') modals.openGestionModal();
  };

  const handleDayAction = (actionId: string, day: number) => {
    const date = calendar.formatDate(day);
    if (actionId === 'reunion') modals.openReunionModal(date);
    else if (actionId === 'propuesta') modals.openPropuestaModal(date);
    else if (actionId === 'gestion') modals.openGestionModal(date);
  };

  const handleCalendarDayClick = (day: number) => {
    const formattedDate = calendar.formatDate(day);
    const calendarEvents = calendarReuniones.map(reunionToCalendarEvent);
    const dayEvents = calendarEvents.filter(e => e.date === formattedDate);

    calendar.setSelectedDate(formattedDate);

    if (calendar.calendarViewMode === 'month' || dayEvents.length > 0) {
      modals.openDayViewModal(formattedDate, dayEvents);
    }
  };

  return (
    <div className="flex min-h-screen bg-[#F3F4F6] font-sans text-[#1F2937] selection:bg-[#5D1828] selection:text-white">

      {/* Modals */}
      <AnimatePresence>
        {modals.isReunionModalOpen && (
          <ReunionModal
            key="reunion-modal"
            isOpen={modals.isReunionModalOpen}
            onClose={modals.closeReunionModal}
            municipalities={MUNICIPALITIES}
            initialDate={modals.modalDefaultDate}
            initialData={modals.selectedReunionData}
            onSave={() => {
              modals.triggerReunionesRefresh();
              refreshCalendar();
            }}
            onSetRefreshFunction={modals.setReunionModalRefresh}
            onOpenPropuesta={() => {
              modals.openPropuestaModal(
                modals.selectedReunionData?.date,
                modals.selectedReunionData || undefined
              );
            }}
            onOpenGestion={() => {
              modals.openGestionModal(
                modals.selectedReunionData?.date,
                modals.selectedReunionData || undefined
              );
            }}
          />
        )}

        {modals.isPropuestaModalOpen && (
          <PropuestaModal
            key="propuesta-modal"
            isOpen={modals.isPropuestaModalOpen}
            onClose={modals.closePropuestaModal}
            municipalities={MUNICIPALITIES}
            initialDate={modals.modalDefaultDate}
            preselectedReunion={modals.selectedReunionForAssignment}
            initialData={modals.selectedPropuestaData}
            sourceReunion={modals.sourceReunionForBack}
            onBackToReunion={modals.backToReunion}
            onSave={async () => {
              modals.triggerPropuestasRefresh();
              refreshCalendar();
              if (modals.reunionModalRefreshRef.current) {
                await modals.reunionModalRefreshRef.current();
              }
            }}
          />
        )}

        {modals.isGestionModalOpen && (
          <GestionModal
            key="gestion-modal"
            isOpen={modals.isGestionModalOpen}
            onClose={modals.closeGestionModal}
            municipalities={MUNICIPALITIES}
            initialDate={modals.modalDefaultDate}
            preselectedReunion={modals.selectedReunionForAssignment}
            initialData={modals.selectedGestionData}
            sourceReunion={modals.sourceReunionForBack}
            onBackToReunion={modals.backToReunion}
            onSave={async () => {
              modals.triggerGestionesRefresh();
              refreshCalendar();
              if (modals.reunionModalRefreshRef.current) {
                await modals.reunionModalRefreshRef.current();
              }
            }}
          />
        )}

        {modals.isDayViewModalOpen && (
          <DayViewModal
            key="day-view-modal"
            isOpen={modals.isDayViewModalOpen}
            onClose={modals.closeDayViewModal}
            date={modals.selectedDayDate}
            events={modals.selectedDayEvents}
            onOpenDetails={modals.openReunionDetails}
            onOpenPropuesta={(e) => {
              modals.openPropuestaModal(e.date || undefined, e);
            }}
            onOpenGestion={(e) => {
              modals.openGestionModal(e.date || undefined, e);
            }}
            onAddReunion={() => {
              modals.openReunionModal(modals.selectedDayDate || undefined);
            }}
          />
        )}
      </AnimatePresence>

      {/* Sidebar - Desktop/Tablet only */}
      {(isTablet || isDesktop) && (
        <Sidebar
          isOpen={isSidebarOpen}
          onToggle={() => setIsSidebarOpen(!isSidebarOpen)}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          logoUrl={LOGO_URL}
        />
      )}

      {/* Main Content */}
      <main className="flex-1 flex flex-col min-h-screen bg-[#F3F4F6]">
        <Header
          isMobile={isMobile}
          isTablet={isTablet}
          onMenuClick={() => setIsSidebarOpen(!isSidebarOpen)}
          isProfileMenuOpen={isProfileMenuOpen}
          setIsProfileMenuOpen={setIsProfileMenuOpen}
          profileMenuRef={profileMenuRef}
          logoUrl={LOGO_URL}
          onSettingsClick={() => setActiveTab('settings' as TabId)}
        />

        {/* Content Area with bottom padding for mobile nav */}
        <div className="flex-1 relative flex flex-col pb-0 md:pb-0" style={{ paddingBottom: isMobile ? '64px' : '0' }}>
          <AnimatePresence mode="wait">
            {/* Agenda Tab */}
            {activeTab === 'agenda' && (
              <motion.div
                key="agenda"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full flex flex-col bg-[#F3F4F6]"
              >
                <AgendaView
                  calendar={calendar}
                  calendarReuniones={calendarReuniones}
                  onActionClick={handleActionClick}
                  onDayAction={handleDayAction}
                  onCalendarDayClick={handleCalendarDayClick}
                  onEventClick={modals.openReunionDetails}
                  isMobile={isMobile}
                  isTablet={isTablet}
                />
              </motion.div>
            )}

            {/* Bitácora Tab */}
            {activeTab === 'bitacora' && (
              <motion.div
                key="bitacora"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full bg-[#F3F4F6]"
              >
                <Suspense fallback={<ViewSuspenseFallback />}>
                  <BitacoraView />
                </Suspense>
              </motion.div>
            )}

            {/* Reuniones Tab */}
            {activeTab === 'reuniones' && (
              <motion.div
                key="reuniones"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full bg-[#F3F4F6]"
              >
                <Suspense fallback={<ViewSuspenseFallback />}>
                  <ReunionesView
                    refreshTrigger={modals.reunionesRefreshTrigger}
                    onNewReunion={() => modals.openReunionModal()}
                    onOpenDetails={modals.openReunionDetails}
                  />
                </Suspense>
              </motion.div>
            )}

            {/* Propuestas Tab */}
            {activeTab === 'propuestas' && (
              <motion.div
                key="propuestas"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full bg-[#F3F4F6]"
              >
                <Suspense fallback={<ViewSuspenseFallback />}>
                  <PropuestasView
                    key={`propuestas-view-${modals.propuestasRefreshTrigger}`}
                    onNewPropuesta={() => modals.openPropuestaModal()}
                    onOpenDetails={(data) => modals.openPropuestaDetails(data)}
                  />
                </Suspense>
              </motion.div>
            )}

            {/* Gestiones Tab */}
            {activeTab === 'gestiones' && (
              <motion.div
                key="gestiones"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="w-full bg-[#F3F4F6]"
              >
                <Suspense fallback={<ViewSuspenseFallback />}>
                  <GestionesView
                    key={`gestiones-view-${modals.gestionesRefreshTrigger}`}
                    onNewGestion={() => modals.openGestionModal()}
                    onOpenDetails={(data) => modals.openGestionDetails(data)}
                  />
                </Suspense>
              </motion.div>
            )}

            {/* Electoral Tab */}
            {activeTab === 'electoral' && (
              <motion.div
                key="electoral"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 z-10 bg-[#F3F4F6]"
              >
                <Suspense fallback={<ViewSuspenseFallback />}>
                  <ElectoralView
                    viewMode={electoralViewMode}
                    setViewMode={setElectoralViewMode}
                    selectedType={selectedElectoralType}
                    setSelectedType={setSelectedElectoralType}
                  />
                </Suspense>
              </motion.div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <motion.div
                key="settings"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="absolute inset-0 z-10 bg-[#F3F4F6]"
              >
                <Suspense fallback={<ViewSuspenseFallback />}>
                  <SettingsView />
                </Suspense>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </main>

      {/* Mobile Bottom Navigation */}
      {isMobile && <BottomNav activeTab={activeTab} onTabChange={setActiveTab} />}

      {/* Session Timeout Warning Modal */}
      <IdleTimeoutWarning
        show={showIdleWarning}
        remainingSeconds={Math.ceil(idleRemainingTime / 1000)}
        onStayLoggedIn={dismissIdleWarning}
        onLogout={signOut}
      />
    </div>
  );
}
